
import './index.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import PrivacyPolicy from './PrivacyPolicy';
import TermsAndConditions from './TermsAndConditions';
import AccountDeletion from './AccountDeletion';
import HowItWorks from './HowItWorks';
import NotFound from './NotFound';

const Hero = () => {
  const containerRef = useRef(null);
  const [sprites, setSprites] = useState([]);
  const [visible, setVisible] = useState([]);
  const [appOpenAttempted, setAppOpenAttempted] = useState(false);
  const [appInstalled, setAppInstalled] = useState(false);
  const emojis = useMemo(() => [
    '⚽️','🏀','🎾','🏐','🏸','🏋️‍♂️','🚴‍♂️','🏊‍♂️','🏹','🥊','🎯','🏆'
  ], []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = Math.floor(rect.width);
    const height = Math.floor(rect.height);
    const count = Math.max(10, Math.min(28, Math.floor((width * height) / 32000)));
    const placed = [];
    const items = [];
    const gap = 24;
    const minSize = 20;
    const maxSize = 56;
    function overlaps(x, y, r){
      for (const p of placed){
        const dx = x - p.x; const dy = y - p.y; const md = r + p.r + gap;
        if (dx*dx + dy*dy < md*md) return true;
      }
      return false;
    }
    for (let i=0;i<count;i++){
      const fontSize = Math.floor(minSize + Math.random() * (maxSize - minSize));
      const r = Math.max(10, Math.floor(fontSize * 0.5));
      const minX = r + 8, maxX = width - r - 8;
      const minY = r + 8, maxY = height - r - 8;
      let tries = 0, ok = false, x=0, y=0;
      while (tries < 50 && !ok){
        x = Math.floor(minX + Math.random() * Math.max(1, (maxX - minX)));
        y = Math.floor(minY + Math.random() * Math.max(1, (maxY - minY)));
        ok = !overlaps(x,y,r);
        tries++;
      }
      if (!ok) continue;
      placed.push({x,y,r});
      items.push({ id: `${i}-${x}-${y}`, e: emojis[Math.floor(Math.random()*emojis.length)], x, y, s: fontSize });
    }
    setSprites(items);
    setVisible([]);
  }, [emojis]);

  useEffect(() => {
    if (!sprites.length) return;
    const timers = sprites.map((sp, idx) => setTimeout(() => setVisible(v => [...v, sp.id]), idx * 60));
    return () => timers.forEach(clearTimeout);
  }, [sprites]);

  // App detection and button handlers
  const handleOpenApp = () => {
    if (appOpenAttempted) {
      showDownloadOptions();
      return;
    }
    
    setAppOpenAttempted(true);
    
    // Try to open the app
    const customSchemeUrl = 'posa://';
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = customSchemeUrl;
    document.body.appendChild(iframe);
    
    // Also try direct window location change
    setTimeout(() => {
      window.location = customSchemeUrl;
    }, 100);
    
    // Set a timer to show download options if app doesn't open
    setTimeout(() => {
      if (!appInstalled) {
        showDownloadOptions();
      }
    }, 2500);
  };

  const showDownloadOptions = () => {
    const downloadOptions = document.getElementById('downloadOptions');
    const appStoreBtn = document.getElementById('appStoreBtn');
    const playStoreBtn = document.getElementById('playStoreBtn');
    
    if (downloadOptions) {
      downloadOptions.style.display = 'block';
    }
    
    // Show appropriate download button based on platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    if (isIOS && appStoreBtn) {
      appStoreBtn.style.display = 'block';
    } else if (isAndroid && playStoreBtn) {
      playStoreBtn.style.display = 'block';
    } else {
      // Desktop - show both options
      if (appStoreBtn) appStoreBtn.style.display = 'block';
      if (playStoreBtn) playStoreBtn.style.display = 'block';
    }
  };

  // App detection
  useEffect(() => {
    const detectAppInstallation = () => {
      const startTime = Date.now();
      
      // Listen for page visibility changes
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          const timeElapsed = Date.now() - startTime;
          if (timeElapsed < 1000) {
            setAppInstalled(false);
          }
        }
      };
      
      // Listen for page focus
      const handleFocus = () => {
        const timeElapsed = Date.now() - startTime;
        if (timeElapsed < 1000) {
          setAppInstalled(false);
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);
      
      // If user doesn't return to page within 2 seconds, assume app opened
      const timer = setTimeout(() => {
        if (document.visibilityState === 'visible') {
          setAppInstalled(true);
        }
      }, 2000);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
        clearTimeout(timer);
      };
    };
    
    const cleanup = detectAppInstallation();
    return cleanup;
  }, []);

  return (
    <section style={{padding: '72px 0 56px', textAlign: 'center', backgroundColor: '#E4F924', position: 'relative', overflow: 'hidden'}} ref={containerRef}>
      {sprites.map(sp => {
        const show = visible.includes(sp.id);
        return (
          <span key={sp.id} style={{
            position: 'absolute', left: sp.x, top: sp.y,
            transform: show ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)',
            fontSize: sp.s, opacity: show ? 1 : 0, transition: 'all .45s cubic-bezier(0.34,1.56,0.64,1)',
            userSelect: 'none', pointerEvents: 'none'
          }}>{sp.e}</span>
        );
      })}
      <div className="container" style={{position:'relative', zIndex: 1, backgroundColor: '#E4F924'}}>
        <img src={process.env.PUBLIC_URL + '/posaicon.svg'} alt="PosaSport" width={80} height={80} />
        <h1 className="hero-title" style={{ color: '#14213D', backgroundColor: '#E4F924'}}>Play more, together</h1>
        <p className="hero-sub" style={{color: '#14213D', backgroundColor: '#E4F924'}}>Discover nearby sports activities, host your own sessions, and manage your venues – all in one place.</p>
        <Link
          to="/how-it-works"
          style={{
            display: 'inline-block',
            marginBottom: '24px',
            padding: '10px 20px',
            borderRadius: '36px',
            border: '2px solid #14213D',
            color: '#14213D',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '15px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          How Posa Works
        </Link>
        <div style={{display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center'}}>
          {/* Open App Button */}
          <div style={{display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center'}}>
            <button 
              id="openAppBtn"
              onClick={handleOpenApp}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '12px 24px',
                background: appOpenAttempted && !appInstalled 
                  ? 'linear-gradient(135deg, #007AFF, #0056CC)' 
                  : 'linear-gradient(135deg, #000000, #333333)',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '36px',
                fontWeight: '600',
                fontSize: '16px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                transition: 'all 0.3s ease',
                border: 'none',
                cursor: 'pointer',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                minWidth: '200px',
                height: '50px'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                e.target.style.background = appOpenAttempted && !appInstalled 
                  ? 'linear-gradient(135deg, #0056CC, #007AFF)' 
                  : 'linear-gradient(135deg, #333333, #000000)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                e.target.style.background = appOpenAttempted && !appInstalled 
                  ? 'linear-gradient(135deg, #007AFF, #0056CC)' 
                  : 'linear-gradient(135deg, #000000, #333333)';
              }}
            >
              
              {appOpenAttempted && !appInstalled ? 'Install Posa Sport' : 'Open Posa Sport'}
            </button>
            
            {/* App Store style badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #E4F924, #B8D91A)',
              borderRadius: '6px',
              border: '1px solid #14213D',
              fontSize: '12px',
              fontWeight: '600',
              color: '#14213D',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
              🏆 #1 Sports App
            </div>
          </div>
          
          {/* Download Options - Initially Hidden */}
          <div id="downloadOptions" style={{display: 'none', textAlign: 'center'}}>
            <p style={{margin: '0 0 16px 0', fontSize: '16px', color: '#14213D'}}>
              Don't have the Posa app yet?
            </p>
            <div style={{display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap'}}>
              <a href="https://apps.apple.com/us/app/posa-sport/id6746412980" aria-label="Download on the App Store" id="appStoreBtn" style={{display: 'none'}}>
                <img src={process.env.PUBLIC_URL + '/appstoreEN.svg'} alt="Download on the App Store" height={44} />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.oxidedigital.posa" aria-label="Get it on Google Play" id="playStoreBtn" style={{display: 'none'}}>
                <img src={process.env.PUBLIC_URL + '/playstoreEN.png'} alt="Get it on Google Play" height={44} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Features = () => (
  <section style={{padding: '32px 0 72px'}}>
    <div className="container">
      <div className="cards">
      {[{
        title: 'Discover',
        text: 'Find activities by date, sport and location with real-time availability.'
      },{
        title: 'Host',
        text: 'Create studio activities or events, manage participants and chat.'
      },{
        title: 'Studios',
        text: 'List and manage your venues; show only your studios and venues.'
      },{
        title: 'Notifications',
        text: 'Stay up to date with joins, messages and schedule changes.'
      }].map((f) => (
        <div key={f.title} className="card">
          <h3 style={{margin: 0, fontSize: 18}}>{f.title}</h3>
          <p style={{margin: '8px 0 0', color: '#555'}}>{f.text}</p>
        </div>
      ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="footer">
    <div className="container">
      <small>
        © {new Date().getFullYear()}{' '}
        <a href="https://www.oxide-digital.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
          Oxide Digital Technology
        </a>
        {' '}• <a href="/direct-app.html?type=profile">Open in App</a> • <Link to="/how-it-works" style={{ color: 'inherit', textDecoration: 'none' }}>How Posa Works</Link> • <Link to="/privacy-policy" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy Policy</Link> • <Link to="/terms-and-conditions" style={{ color: 'inherit', textDecoration: 'none' }}>Terms and Conditions</Link> • <Link to="/account-deletion" style={{ color: 'inherit', textDecoration: 'none' }}>Account Deletion</Link>
      </small>
    </div>
  </footer>
);

const Home = () => (
  <>
    <Hero />
    <Features />
    <Footer />
  </>
);

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/how-it-works" element={<><HowItWorks /><Footer /></>} />
        <Route path="/privacy-policy" element={<><PrivacyPolicy /><Footer /></>} />
        <Route path="/terms-and-conditions" element={<><TermsAndConditions /><Footer /></>} />
        <Route path="/account-deletion" element={<><AccountDeletion /><Footer /></>} />
        <Route path="*" element={<><NotFound /><Footer /></>} />
      </Routes>
    </Router>
  );
}
